import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchAdminSymbols,
  reorderAdminSymbols,
  updateAdminSymbol,
  type SupportedSymbol,
} from '../../lib/symbols';
import { AdminSymbolRow } from './AdminSymbolRow';
import { MassiveTickerSearch } from './MassiveTickerSearch';

interface AdminViewProps {
  onSymbolsChanged: () => Promise<void>;
}

interface StatusMessage {
  tone: 'info' | 'success' | 'error';
  text: string;
}

export function AdminView({ onSymbolsChanged }: AdminViewProps) {
  const [symbols, setSymbols] = useState<SupportedSymbol[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [draggingTicker, setDraggingTicker] = useState<string | null>(null);
  const dragStartOrderRef = useRef<string[] | null>(null);
  const symbolsRef = useRef<SupportedSymbol[] | null>(null);

  useEffect(() => {
    symbolsRef.current = symbols;
  }, [symbols]);

  const loadSymbols = useCallback(
    () =>
      fetchAdminSymbols().then(
        (loaded) => {
          setLoadError(null);
          setSymbols(loaded);
        },
        (error: unknown) => {
          setLoadError(error instanceof Error ? error.message : 'Failed to load symbols');
        },
      ),
    [],
  );

  useEffect(() => {
    void loadSymbols();
  }, [loadSymbols]);

  const handleToggleEnabled = useCallback(
    async (ticker: string, enabled: boolean) => {
      const updated = await updateAdminSymbol(ticker, { enabled });
      setSymbols((current) =>
        current == null
          ? current
          : current.map((symbol) => (symbol.ticker === ticker ? updated : symbol)),
      );
      await onSymbolsChanged();
    },
    [onSymbolsChanged],
  );

  const handleCatalogChanged = useCallback(async () => {
    await loadSymbols();
    await onSymbolsChanged();
  }, [loadSymbols, onSymbolsChanged]);

  const handleDragStart = useCallback(
    (ticker: string) => {
      setDraggingTicker(ticker);
      dragStartOrderRef.current = symbols?.map((symbol) => symbol.ticker) ?? null;
    },
    [symbols],
  );

  const handleDragEnter = useCallback(
    (targetTicker: string) => {
      const current = symbolsRef.current;
      if (current == null || draggingTicker == null || draggingTicker === targetTicker) {
        return;
      }

      const fromIndex = current.findIndex((symbol) => symbol.ticker === draggingTicker);
      const toIndex = current.findIndex((symbol) => symbol.ticker === targetTicker);
      if (fromIndex < 0 || toIndex < 0) {
        return;
      }
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      // Sync the ref immediately: drop can fire before the passive effect that
      // mirrors state into symbolsRef, and the drop handler compares the ref.
      symbolsRef.current = next;
      setSymbols(next);
    },
    [draggingTicker],
  );

  const handleDragEnd = useCallback(() => {
    const startOrder = dragStartOrderRef.current;
    dragStartOrderRef.current = null;
    setDraggingTicker(null);

    const current = symbolsRef.current;
    if (current == null || startOrder == null) {
      return;
    }

    const order = current.map((symbol) => symbol.ticker);
    if (order.join(',') === startOrder.join(',')) {
      return;
    }

    setStatus({ tone: 'info', text: 'Saving order...' });
    reorderAdminSymbols(order).then(
      (saved) => {
        setSymbols(saved);
        setStatus({ tone: 'success', text: 'Order saved' });
        void onSymbolsChanged();
      },
      (error: unknown) => {
        setStatus({
          tone: 'error',
          text: error instanceof Error ? error.message : 'Failed to save symbol order',
        });
        void loadSymbols();
      },
    );
  }, [loadSymbols, onSymbolsChanged]);

  const enabledCount = symbols?.filter((symbol) => symbol.enabled).length ?? 0;
  const getNextSortOrder = useCallback(
    () =>
      symbols != null && symbols.length > 0
        ? Math.max(...symbols.map((symbol) => symbol.sortOrder)) + 10
        : 100,
    [symbols],
  );

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="mx-auto w-full max-w-2xl px-4 py-6 min-[640px]:px-6 min-[640px]:py-8">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="font-mono text-sm font-semibold tracking-[0.08em] text-neutral-100">
              SYMBOL CATALOG
            </h1>
            <p className="mt-1 font-mono text-[11px] leading-relaxed text-neutral-500">
              Tickers available across Heatmaps and Trinity. Search Massive below to add symbols;
              each is validated for a usable options chain before going live. Drag rows to set the
              picker order.
            </p>
          </div>
          {symbols != null && (
            <span className="rounded-md border border-white/[.08] bg-white/[.03] px-2 py-1 font-mono text-[10px] text-neutral-400">
              {enabledCount}/{symbols.length} enabled
            </span>
          )}
        </div>

        <div className="mt-5">
          <MassiveTickerSearch
            onCatalogChanged={handleCatalogChanged}
            getNextSortOrder={getNextSortOrder}
          />
        </div>

        {status != null && (
          <p
            role="status"
            className={`mt-4 font-mono text-[10px] ${
              status.tone === 'error'
                ? 'text-red-400'
                : status.tone === 'success'
                  ? 'text-green-500'
                  : 'text-neutral-500'
            }`}
          >
            {status.text}
          </p>
        )}

        <div className="mt-4 rounded-md border border-white/[.08] bg-white/[.02]">
          <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-white/[.08] px-3 py-2 font-mono text-[9px] font-semibold tracking-[0.08em] text-neutral-600">
            <span className="w-3.5" />
            <span>TICKER</span>
            <span className="w-8 text-center">LIVE</span>
          </div>

          {loadError != null ? (
            <div className="flex flex-col items-center gap-3 px-3 py-8 text-center">
              <p className="font-mono text-[11px] text-red-400">{loadError}</p>
              <button
                type="button"
                onClick={() => {
                  setSymbols(null);
                  setLoadError(null);
                  void loadSymbols();
                }}
                className="inline-flex h-7 items-center rounded-md border border-white/[.08] bg-white/[.03] px-2.5 font-mono text-[10px] font-semibold text-neutral-300 transition-all duration-150 hover:bg-white/[.08] hover:text-neutral-100"
              >
                Retry
              </button>
            </div>
          ) : symbols == null ? (
            <SymbolListSkeleton />
          ) : symbols.length === 0 ? (
            <p className="px-3 py-8 text-center font-mono text-[11px] text-neutral-500">
              No symbols yet. Search Massive above to add some.
            </p>
          ) : (
            symbols.map((symbol) => (
              <AdminSymbolRow
                key={symbol.ticker}
                symbol={symbol}
                isDragging={draggingTicker === symbol.ticker}
                onToggleEnabled={handleToggleEnabled}
                onDragStart={handleDragStart}
                onDragEnter={handleDragEnter}
                onDragEnd={handleDragEnd}
              />
            ))
          )}
        </div>
      </div>
    </main>
  );
}

function SymbolListSkeleton() {
  return (
    <div aria-hidden="true" className="animate-pulse">
      {Array.from({ length: 6 }, (_, index) => (
        <div
          key={index}
          className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-white/[.05] px-3 py-2.5 last:border-b-0"
        >
          <span className="h-3.5 w-3.5 rounded bg-white/[.04]" />
          <span className="h-3 w-16 rounded bg-white/[.06]" />
          <span className="h-4 w-8 rounded-full bg-white/[.06]" />
        </div>
      ))}
    </div>
  );
}
